import { Info, RefreshCw, X } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/shadcn/badge'
import { Button } from '@/components/ui/shadcn/button'

interface VersionUpdateToastContentProps {
	newVersion: string
	onRefresh: () => void
	onDismiss: () => void
}

function VersionUpdateToastContent({
	newVersion: _newVersion,
	onRefresh,
	onDismiss,
}: VersionUpdateToastContentProps) {
	return (
		<div className="flex relative gap-4 items-start p-5 pr-12 w-full max-w-lg">
			{/* Icon */}
			<div className="flex shrink-0 justify-center items-center w-10 h-10 bg-blue-100 rounded-full ring-2 ring-blue-50">
				<Info className="w-5 h-5 text-blue-600" />
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<div className="flex gap-2 items-center mb-2">
					<h4 className="text-base font-semibold text-gray-900">
						New Version Available
					</h4>
					<Badge
						variant="outline"
						className="px-2 py-1 text-xs text-green-700 bg-green-50 border-green-200"
					>
						Latest
					</Badge>
				</div>
				<p className="mb-4 text-sm leading-relaxed text-gray-600">
					A new version of the application is ready. Refresh to get
					the latest features and fixes.
				</p>

				{/* Actions */}
				<div className="flex gap-3 items-center">
					<Button
						size="sm"
						onClick={onRefresh}
						className="px-4 h-9 text-sm font-semibold text-white bg-blue-600 shadow-md transition-all duration-200 transform hover:bg-blue-700 hover:shadow-lg hover:scale-105"
					>
						<RefreshCw className="mr-2 w-4 h-4" />
						Refresh Now
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={onDismiss}
						className="px-3 h-9 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100"
					>
						Later
					</Button>
				</div>
			</div>

			{/* Close button */}
			<button
				type="button"
				onClick={onDismiss}
				className="flex absolute top-3 right-3 shrink-0 justify-center items-center w-6 h-6 rounded-full transition-colors hover:bg-gray-100"
			>
				<X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
			</button>
		</div>
	)
}

/**
 * Handle refresh action - clear caches and reload
 */
function handleRefresh() {
	// Clear any cached data that might be causing issues
	if ('caches' in window) {
		caches.keys().then((names) => {
			names.forEach((name) => caches.delete(name))
		})
	}

	// Force refresh
	window.location.reload()
}

/**
 * Handle dismiss action
 */
function handleDismiss(toastId: string | number) {
	toast.dismiss(toastId)
}

/**
 * Show a beautiful shadcn-based toast notification for version updates
 */
export function showVersionUpdateToast(newVersion: string) {
	const toastId = toast.custom(
		(id) => (
			<VersionUpdateToastContent
				newVersion={newVersion}
				onRefresh={handleRefresh}
				onDismiss={() => handleDismiss(id)}
			/>
		),
		{
			id: 'version-update',
			duration: Number.POSITIVE_INFINITY, // Don't auto-dismiss
			position: 'bottom-right',
			className: 'version-update-toast',
			style: {
				background: 'white',
				border: '1px solid #e5e7eb',
				borderRadius: '12px',
				boxShadow:
					'0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
				padding: 0,
			},
		}
	)

	return toastId
}

/**
 * Show a success toast when version is updated
 */
export function showVersionUpdatedToast() {
	toast.success('Application updated successfully!', {
		description: 'You are now running the latest version.',
		duration: 3000,
		position: 'bottom-right',
	})
}

/**
 * Show an error toast if version check fails
 */
export function showVersionCheckErrorToast() {
	toast.error('Failed to check for updates', {
		description:
			'Unable to connect to update server. Please try again later.',
		duration: 5000,
		position: 'bottom-right',
	})
}

export default VersionUpdateToastContent
